import { v4 as uuid } from 'uuid'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { FULL_DAY_VIEW_INTERVAL } from 'Data/constants.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  return async function onSubmit({ value, args, change }) {
    switch (args.type) {
      case 'setLocation': {
        if (
          value.selected.location_id === args.location_id &&
          value.selected.vaxiom_location_id === args.vaxiom_location_id &&
          value.selected.time_zone_id === args.time_zone_id
        )
          return

        change(next => {
          // Change location
          next.selected.location_id = args.location_id
          next.selected.vaxiom_location_id = args.vaxiom_location_id
          next.selected.location_name = args.location_name

          // Update date according to new location timezone
          let currentUtcDate = zonedTimeToUtc(
            value.selected.date,
            value.selected.time_zone_id
          )
          next.selected.date = utcToZonedTime(currentUtcDate, args.time_zone_id)
          next.selected.time_zone_id = args.time_zone_id

          // Reset scheduling
          next.scheduling = { ...SCHEDULING_ORIGINAL_VALUE }

          if (args.show_loading_overlay) {
            next.appointment_overlay.is_location_changing = true
          }
        })

        break
      }
      case 'setDate': {
        change(next => {
          next.selected.date = args.date
          next.scheduling = { ...SCHEDULING_ORIGINAL_VALUE }
        })
        break
      }
      case 'setOptimalDate': {
        change(next => {
          next.selected.date = args.date
          next.optimal_date = args.date
        })
        break
      }
      case 'appointmentOverlay/open': {
        change(next => {
          next.appointment_overlay = {
            // This updated_as value helps us to refresh the appointment overlay data
            // if the user is trying to open it with a diffrent patient or flow when already opened
            updated_at: Date.now(),
            open: true,
            mode: 'normal',
            appointment_id: args.appointment_id,
            patient_id: args.patient_id || null,
            treatment_id: args.treatment_id || null,
            is_location_changing: false,
            is_patient_overlay_call: !!args.is_patient_overlay_call,
            is_external_patient_id: !!args.is_external_patient_id,
            is_external_appointment_id: !!args.is_external_appointment_id,
            is_external_treatment_id: !!args.is_external_treatment_id,
            note: '',
            appointment_type_id: null,
            appointment_type_name: null,
            duration: null,
            patient_option: null,
            patient_new: {
              provider_employee_resource_id: null,
              referral_source_id: null,
              person: {
                title: null,
                gender: null,
                first_name: null,
                middle_name: null,
                last_name: null,
                birth_date: null,
                ssn: null,
              },
            },
            ...('mode' in args && { mode: args.mode }),
          }
          next.scheduling = {
            ...SCHEDULING_ORIGINAL_VALUE,
          }
          next.optimal_date = null
          if (next.selected.full_day_default) {
            next.selected.slot_interval = FULL_DAY_VIEW_INTERVAL
          }

          if ('date' in args) {
            next.selected.date = args.date
          }
          if ('location' in args) {
            next.selected.location_id = args.location.location_id
            next.selected.vaxiom_location_id = args.location.vaxiom_location_id
            next.selected.location_name = args.location.location_name
            next.selected.time_zone_id = args.location.time_zone_id
          }
        })
        break
      }
      case 'appointmentOverlay/close': {
        change(next => {
          next.appointment_overlay.open = false
          next.appointment_overlay.mode = 'normal'
          next.appointment_overlay.appointment_id = null
          next.appointment_overlay.patient_id = null
          next.appointment_overlay.treatment_id = null
          next.appointment_overlay.is_patient_overlay_call = false
          next.appointment_overlay.note = ''
          next.appointment_overlay.appointment_type_id = null
          next.appointment_overlay.appointment_type_name = null
          next.appointment_overlay.duration = null
          next.appointment_overlay.patient_option = null
          next.appointment_overlay.patient_new = {
            provider_employee_resource_id: null,
            referral_source_id: null,
            person: {
              title: null,
              gender: null,
              first_name: null,
              middle_name: null,
              last_name: null,
              birth_date: null,
              ssn: null,
            },
            person_referrals: [],
            template_referrals: [],
            referrals: [],
          }
          next.scheduling = {
            ...SCHEDULING_ORIGINAL_VALUE,
          }
          next.optimal_date = null
          next.selected.slot_interval = next.selected.slot_interval_settings
        })
        break
      }
      case 'scheduling/selectUntemplatedSlot': {
        change(next => {
          let id = `${uuid()}-candidate-untemplated`
          next.scheduling.slot_id = null
          next.scheduling.untemplated_slot = {
            id,
            start_min: args.start_min,
            end_min: args.end_min,
            chair_id: args.chair_id,
            chair_name: args.chair_name,
            location_id: next.selected.location_id,
            date: args.date,
          }
          if (args.focus) next.focused_event_id = id
          if (args.slot_interval)
            next.selected.slot_interval = args.slot_interval
        })
        break
      }
      case 'scheduling/setTemplatedSlots': {
        let id = args.slot_id
          ? args.slot_id
          : args.untemplated_slot
          ? `${uuid()}-candidate-untemplated`
          : null

        change(next => {
          next.scheduling = {
            appointment_id: args.appointment_id,
            appointment_type_name: args.appointment_type_name,
            slots: args.slots,
            duration: args.duration,
            slot_id: args.slot_id ? id : null,
            untemplated_slot: args.untemplated_slot
              ? {
                  id,
                  start_min: args.untemplated_slot.start_min,
                  end_min: args.untemplated_slot.end_min,
                  chair_id: args.untemplated_slot.chair_id,
                  chair_name: args.untemplated_slot.chair_name,
                  date: args.untemplated_slot.date,
                  location_id: next.selected.location_id,
                }
              : next.scheduling.untemplated_slot,
          }
          next.focused_event_id = id
        })
        break
      }
      case 'scheduling/selectTemplatedSlot': {
        change(next => {
          next.scheduling.slot_id = args.id
          next.scheduling.untemplated_slot = {
            ...SCHEDULING_ORIGINAL_VALUE.untemplated_slot,
          }
          if (args.focus) next.focused_event_id = args.id
          if (args.slot_interval)
            next.selected.slot_interval = args.slot_interval
        })
        break
      }
      case 'scheduling/deselectAnySlot': {
        change(next => {
          next.scheduling.slot_id = null
          next.scheduling.untemplated_slot = {
            ...SCHEDULING_ORIGINAL_VALUE.untemplated_slot,
          }
        })
        break
      }
      case 'scheduling/reset': {
        change(next => {
          next.scheduling = {
            ...SCHEDULING_ORIGINAL_VALUE,
          }

          if ('optimal_date' in args) next.optimal_date = args.optimal_date
          if ('date' in args) next.selected.date = args.date
          if ('location' in args) {
            next.selected.location_id = args.location.location_id
            next.selected.vaxiom_location_id = args.location.vaxiom_location_id
            next.selected.location_name = args.location.location_name
            next.selected.time_zone_id = args.location.time_zone_id
          }
        })
        break
      }
      case 'scheduling/setSlotInterval': {
        change(next => {
          next.selected.slot_interval = args.slot_interval
        })
        break
      }
      case 'removeFocus': {
        change(next => {
          next.focused_event_id = null
        })
        break
      }
      case 'appointmentQueue/add': {
        change(next => {
          next.is_dragging_timegrid_appointment = false
          next.dragged_appointment = null
          if (
            !next.selected.appointment_queue.some(
              item => item.id === args.event.id
            )
          ) {
            next.selected.appointment_queue = [
              ...next.selected.appointment_queue,
              {
                ...args.event,
              },
            ]
          }
        })
        break
      }
      case 'appointmentQueue/remove': {
        change(next => {
          next.selected.appointment_queue =
            next.selected.appointment_queue.filter(item => item.id !== args.id)
        })
        break
      }
      default: {
        break
      }
    }
  }
}

let SCHEDULING_ORIGINAL_VALUE = {
  appointment_id: null,
  slots: [],
  slot_id: null,
  duration: null,
  appointment_type_name: '',
  untemplated_slot: {
    id: null,
    date: null,
    start_min: null,
    end_min: null,
    chair_id: null,
    chair_name: null,
    location_id: null,
  },
}
